import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Typography,
    Sheet,
    Card,
    CardContent,
    Box,
    IconButton,
    Stack,
    Alert,
    CircularProgress,
    Chip,
    Button,
    Grid,
} from '@mui/joy';
import {
    DeviceHub,
    ArrowBack,
    Timeline,
    Refresh,
    ChevronLeft,
    ChevronRight,
    SmartToy,
    Videocam,
    Computer,
    Memory,
    Thermostat,
    Air,
    BatteryChargingFull,
    Wifi,
    Storage,
} from '@mui/icons-material';
import { getResourceUtilization, getEvents } from '../services/observabilityAdminApi';
import AIProcessingTimeLane from '../components/telemetry/AIProcessingTimeLane';
import EventsCompactFeed from '../components/telemetry/EventsCompactFeed';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

// Chart constants
const CHART_WIDTH = 800;
const CHART_HEIGHT = 100;

// Timeline ruler component
const TimelineRuler = ({ startTime, endTime }) => {
    const duration = endTime - startTime;
    const hourMarks = [];
    const halfHourMarks = [];
    const minuteMarks = [];

    // Generate hour marks
    for (let i = 0; i <= Math.ceil(duration / (60 * 60 * 1000)); i++) {
        const time = startTime + (i * 60 * 60 * 1000);
        if (time <= endTime) {
            hourMarks.push({
                time,
                position: ((time - startTime) / duration) * 100,
                label: new Date(time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
            });
        }
    }

    // Generate 30-minute marks
    for (let i = 0; i <= Math.ceil(duration / (30 * 60 * 1000)); i++) {
        const time = startTime + (i * 30 * 60 * 1000);
        if (time <= endTime && !hourMarks.some(mark => mark.time === time)) {
            halfHourMarks.push({
                time,
                position: ((time - startTime) / duration) * 100
            });
        }
    }

    // Generate 5-minute marks
    for (let i = 0; i <= Math.ceil(duration / (5 * 60 * 1000)); i++) {
        const time = startTime + (i * 5 * 60 * 1000);
        if (time <= endTime && !hourMarks.some(mark => mark.time === time) && !halfHourMarks.some(mark => mark.time === time)) {
            minuteMarks.push({
                time,
                position: ((time - startTime) / duration) * 100
            });
        }
    }

    return (
        <Box sx={{ height: '60px', position: 'relative', borderBottom: '1px solid', borderColor: 'neutral.300', mb: 2 }}>
            {/* Hour marks */}
            {hourMarks.map((mark, index) => {
                // Determine label positioning
                let labelStyle;
                if (index === 0) {
                    // First mark: position from inside left
                    labelStyle = {
                        position: 'absolute', 
                        top: '5px', 
                        left: '5px',
                        fontWeight: 'bold',
                        color: 'primary.700'
                    };
                } else if (index === hourMarks.length - 1) {
                    // Last mark: position from inside right
                    labelStyle = {
                        position: 'absolute', 
                        top: '5px', 
                        right: '5px',
                        fontWeight: 'bold',
                        color: 'primary.700'
                    };
                } else {
                    // Middle marks: centered
                    labelStyle = {
                        position: 'absolute', 
                        top: '5px', 
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold',
                        color: 'primary.700'
                    };
                }

                return (
                    <Box key={`hour-${index}`} sx={{ 
                        position: 'absolute', 
                        left: `${mark.position}%`, 
                        top: 0, 
                        height: '100%',
                        borderLeft: '2px solid',
                        borderColor: 'primary.500'
                    }}>
                        <Typography level="body-xs" sx={labelStyle}>
                            {mark.label}
                        </Typography>
                    </Box>
                );
            })}
            
            {/* 30-minute marks */}
            {halfHourMarks.map((mark, index) => (
                <Box key={`half-${index}`} sx={{ 
                    position: 'absolute', 
                    left: `${mark.position}%`, 
                    top: '10px', 
                    height: 'calc(100% - 10px)',
                    borderLeft: '1px solid',
                    borderColor: 'neutral.500'
                }} />
            ))}
            
            {/* 5-minute marks */}
            {minuteMarks.map((mark, index) => (
                <Box key={`min-${index}`} sx={{ 
                    position: 'absolute', 
                    left: `${mark.position}%`, 
                    top: '20px', 
                    height: 'calc(100% - 20px)',
                    borderLeft: '1px solid',
                    borderColor: 'neutral.300'
                }} />
            ))}
        </Box>
    );
};

// AI Processing Lane with outlier detection
const AIProcessingLane = ({ aiData, startTime, endTime, rulerDuration }) => {
    console.log('🎨 AIProcessingLane render with data:', aiData?.length);
    
    if (!aiData || aiData.length === 0) {
        return (
            <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'neutral.400' }}>
                No AI processing data
            </Box>
        );
    }

    const relevantData = aiData.filter(item => {
        const procStart = item.processing_start_ms;
        const procEnd = item.processing_end_ms;
        return procStart && procEnd && 
               ((procStart >= startTime && procStart <= endTime) ||
                (procEnd >= startTime && procEnd <= endTime) ||
                (procStart <= startTime && procEnd >= endTime));
    });

    if (relevantData.length === 0) {
        return (
            <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'neutral.400' }}>
                No data in this time range
            </Box>
        );
    }

    // Extract efficiency values for outlier detection
    const efficiencyValues = relevantData
        .map(item => item.pipeline_efficiency_pc)
        .filter(val => val != null && !isNaN(val))
        .sort((a, b) => a - b);

    if (efficiencyValues.length === 0) {
        return (
            <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'neutral.400' }}>
                No efficiency data
            </Box>
        );
    }

    // Outlier detection using IQR method
    const q1Index = Math.floor(efficiencyValues.length * 0.25);
    const q3Index = Math.floor(efficiencyValues.length * 0.75);
    const q1 = efficiencyValues[q1Index];
    const q3 = efficiencyValues[q3Index];
    const iqr = q3 - q1;
    const outlierThreshold = q3 + (1.5 * iqr); // Standard outlier detection
    
    console.log('📊 Efficiency stats:', {
        count: efficiencyValues.length,
        q1: q1?.toFixed(1),
        q3: q3?.toFixed(1),
        iqr: iqr?.toFixed(1),
        threshold: outlierThreshold?.toFixed(1)
    });

    // Split data into normal and outliers
    const normalData = relevantData.filter(item => 
        item.pipeline_efficiency_pc != null && 
        !isNaN(item.pipeline_efficiency_pc) && 
        item.pipeline_efficiency_pc <= outlierThreshold
    );
    
    const outlierData = relevantData.filter(item => 
        item.pipeline_efficiency_pc != null && 
        !isNaN(item.pipeline_efficiency_pc) && 
        item.pipeline_efficiency_pc > outlierThreshold
    );

    // Calculate medians
    const normalEfficiencies = normalData.map(item => item.pipeline_efficiency_pc).sort((a, b) => a - b);
    const outlierEfficiencies = outlierData.map(item => item.pipeline_efficiency_pc).sort((a, b) => a - b);
    
    const normalMedian = normalEfficiencies.length > 0 ? 
        (normalEfficiencies.length % 2 === 0 ? 
            (normalEfficiencies[normalEfficiencies.length / 2 - 1] + normalEfficiencies[normalEfficiencies.length / 2]) / 2 :
            normalEfficiencies[Math.floor(normalEfficiencies.length / 2)]
        ) : null;
        
    const outlierMedian = outlierEfficiencies.length > 0 ? 
        (outlierEfficiencies.length % 2 === 0 ? 
            (outlierEfficiencies[outlierEfficiencies.length / 2 - 1] + outlierEfficiencies[outlierEfficiencies.length / 2]) / 2 :
            outlierEfficiencies[Math.floor(outlierEfficiencies.length / 2)]
        ) : null;

    // Find max efficiency for normal data scaling
    const maxNormalEfficiency = normalEfficiencies.length > 0 ? 
        Math.max(300, Math.max(...normalEfficiencies) * 1.1) : 300; // At least 300% scale

    return (
        <Box>
            {/* Normal Performance Chart - Original Style */}
            {normalData.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography level="body-sm" sx={{ mb: 1, color: 'neutral.700', fontWeight: 'bold' }}>
                        Normal Performance ({normalData.length} records)
                        {normalMedian && (
                            <Typography component="span" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.500' }}>
                                • Median: {normalMedian.toFixed(1)}%
                            </Typography>
                        )}
                    </Typography>
                    
                    <Box sx={{ 
                        height: '100px', 
                        backgroundColor: 'neutral.50', 
                        borderRadius: 'sm',
                        position: 'relative',
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'neutral.200'
                    }}>
                        <svg 
                            width="100%" 
                            height="100%" 
                            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                            preserveAspectRatio="none"
                            style={{ position: 'absolute' }}
                        >
                            {/* Background grid */}
                            <defs>
                                <pattern id="ai-grid" width="40" height="20" patternUnits="userSpaceOnUse">
                                    <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#e0e0e0" strokeWidth="0.5" opacity="0.3"/>
                                </pattern>
                            </defs>
                            <rect width={CHART_WIDTH} height={CHART_HEIGHT} fill="url(#ai-grid)" />
                            
                            {/* AI Processing blocks */}
                            {normalData.map((record, index) => {
                                // Calculate block position and size
                                const blockStart = Math.max(record.processing_start_ms, startTime);
                                const blockEnd = Math.min(record.processing_end_ms, endTime);
                                
                                const xStart = ((blockStart - startTime) / rulerDuration) * CHART_WIDTH;
                                const xEnd = ((blockEnd - startTime) / rulerDuration) * CHART_WIDTH;
                                const width = xEnd - xStart;
                                
                                // Height based on pipeline efficiency
                                const efficiency = record.pipeline_efficiency_pc;
                                const height = Math.min((efficiency / maxNormalEfficiency) * CHART_HEIGHT * 0.8, CHART_HEIGHT * 0.9);
                                const y = CHART_HEIGHT - height;
                                
                                // Color based on efficiency
                                let fillColor;
                                if (efficiency <= 100) {
                                    fillColor = '#4caf50'; // Green - efficient
                                } else if (efficiency <= 200) {
                                    fillColor = '#ff9800'; // Orange - moderate
                                } else {
                                    fillColor = '#f44336'; // Red - inefficient
                                }
                                
                                return (
                                    <rect
                                        key={record.id || index}
                                        x={xStart}
                                        y={y}
                                        width={Math.max(width, 2)}
                                        height={height}
                                        fill={fillColor}
                                        opacity="0.8"
                                        stroke="#333"
                                        strokeWidth="0.5"
                                    />
                                );
                            })}
                            
                            {/* Median line */}
                            {normalMedian && (
                                <line
                                    x1={0}
                                    y1={CHART_HEIGHT - (normalMedian / maxNormalEfficiency) * CHART_HEIGHT * 0.8}
                                    x2={CHART_WIDTH}
                                    y2={CHART_HEIGHT - (normalMedian / maxNormalEfficiency) * CHART_HEIGHT * 0.8}
                                    stroke="#1976d2"
                                    strokeWidth="1"
                                />
                            )}
                        </svg>
                        
                        {/* Y-axis efficiency indicator */}
                        <Typography level="body-xs" sx={{ 
                            position: 'absolute', 
                            top: '5px', 
                            left: '5px',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            padding: '2px 4px',
                            borderRadius: '2px',
                            fontSize: '10px'
                        }}>
                            {maxNormalEfficiency.toFixed(0)}%
                        </Typography>
                        <Typography level="body-xs" sx={{ 
                            position: 'absolute', 
                            bottom: '5px', 
                            left: '5px',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            padding: '2px 4px',
                            borderRadius: '2px',
                            fontSize: '10px'
                        }}>
                            0%
                        </Typography>
                        
                        {/* Median label on the right */}
                        {normalMedian && (
                            <Typography level="body-xs" sx={{ 
                                position: 'absolute', 
                                top: `${100 - (normalMedian / maxNormalEfficiency) * 80}px`,
                                right: '5px',
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                padding: '2px 4px',
                                borderRadius: '2px',
                                fontSize: '10px',
                                color: '#1976d2',
                                fontWeight: 'bold'
                            }}>
                                Median: {normalMedian.toFixed(1)}%
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            
            {/* Outliers Chart - Smaller, Full Width */}
            {outlierData.length > 0 && (
                <Box sx={{ mb: 1 }}>
                    <Typography level="body-sm" sx={{ mb: 1, color: 'warning.600', fontWeight: 'bold' }}>
                        Performance Outliers ({outlierData.length} records)
                        {outlierMedian && (
                            <Typography component="span" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.500' }}>
                                • Median: {outlierMedian.toFixed(1)}%
                            </Typography>
                        )}
                    </Typography>
                    
                    <Box sx={{ 
                        height: '60px', 
                        backgroundColor: 'warning.50', 
                        borderRadius: 'sm',
                        position: 'relative',
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'warning.200'
                    }}>
                        <svg 
                            width="100%" 
                            height="100%" 
                            viewBox={`0 0 ${CHART_WIDTH} 60`}
                            preserveAspectRatio="none"
                            style={{ position: 'absolute' }}
                        >
                            {/* Background grid */}
                            <defs>
                                <pattern id="outlier-grid" width="40" height="15" patternUnits="userSpaceOnUse">
                                    <path d="M 40 0 L 0 0 0 15" fill="none" stroke="#f0a500" strokeWidth="0.5" opacity="0.3"/>
                                </pattern>
                            </defs>
                            <rect width={CHART_WIDTH} height="60" fill="url(#outlier-grid)" />
                            
                            {/* Outlier blocks */}
                            {outlierData.map((record, index) => {
                                const blockStart = Math.max(record.processing_start_ms, startTime);
                                const blockEnd = Math.min(record.processing_end_ms, endTime);
                                
                                const xStart = ((blockStart - startTime) / rulerDuration) * CHART_WIDTH;
                                const xEnd = ((blockEnd - startTime) / rulerDuration) * CHART_WIDTH;
                                const width = xEnd - xStart;
                                
                                const efficiency = record.pipeline_efficiency_pc;
                                const maxOutlierEfficiency = Math.max(...outlierEfficiencies) * 1.1;
                                const height = Math.min((efficiency / maxOutlierEfficiency) * 50, 55);
                                const y = 60 - height - 5;
                                
                                return (
                                    <rect
                                        key={record.id || index}
                                        x={xStart}
                                        y={y}
                                        width={Math.max(width, 2)}
                                        height={height}
                                        fill="#f44336"
                                        opacity="0.9"
                                        stroke="#d32f2f"
                                        strokeWidth="0.5"
                                    />
                                );
                            })}
                            
                            {/* Outlier median line */}
                            {outlierMedian && (
                                <line
                                    x1={0}
                                    y1={60 - (outlierMedian / Math.max(...outlierEfficiencies) / 1.1) * 50 - 5}
                                    x2={CHART_WIDTH}
                                    y2={60 - (outlierMedian / Math.max(...outlierEfficiencies) / 1.1) * 50 - 5}
                                    stroke="#1976d2"
                                    strokeWidth="1"
                                />
                            )}
                        </svg>
                        
                        {/* Y-axis for outliers */}
                        <Typography level="body-xs" sx={{ 
                            position: 'absolute', 
                            top: '3px', 
                            left: '5px',
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            padding: '1px 3px',
                            borderRadius: '2px',
                            fontSize: '9px'
                        }}>
                            {Math.max(...outlierEfficiencies).toFixed(0)}%
                        </Typography>
                        
                        {/* Outlier median label on the right */}
                        {outlierMedian && (
                            <Typography level="body-xs" sx={{ 
                                position: 'absolute', 
                                top: `${60 - (outlierMedian / Math.max(...outlierEfficiencies) / 1.1) * 50 - 5}px`,
                                right: '5px',
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                padding: '1px 3px',
                                borderRadius: '2px',
                                fontSize: '9px',
                                color: '#1976d2',
                                fontWeight: 'bold'
                            }}>
                                Median: {outlierMedian.toFixed(0)}%
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// Camera Recording Lane component
const CameraRecordingLane = ({ camera, startTime, endTime }) => {
    const duration = endTime - startTime;
    
    // Debug logging to see what data we have
    console.log('CameraRecordingLane Debug:', {
        cameraId: camera.id,
        cameraName: camera.device_name || camera.hub_camera_id,
        historyLength: camera.history?.length || 0,
        sampleHistory: camera.history?.slice(0, 2),
        startTime: new Date(startTime).toLocaleString(),
        endTime: new Date(endTime).toLocaleString()
    });
    
    // Filter recordings that overlap with our time window
    // Try different field name patterns to handle API response variations
    const relevantRecordings = (camera.history || []).filter(recording => {
        // Handle different possible field names from API
        const recStart = recording.started_at || recording.start_ms || recording.start_time;
        const recEnd = recording.ended_at || recording.end_ms || recording.end_time || 
                      (recording.started_at || recording.start_ms) + (recording.duration_ms || 0);
        
        if (!recStart) {
            console.log('Recording missing start time:', recording);
            return false;
        }
        
        // If still recording (no end time), use current time
        const effectiveEnd = recEnd || Date.now();
        
        const overlaps = recStart < endTime && effectiveEnd > startTime;
        
        if (overlaps) {
            console.log('Found overlapping recording:', {
                recStart: new Date(recStart).toLocaleString(),
                recEnd: recEnd ? new Date(recEnd).toLocaleString() : 'ongoing',
                effectiveEnd: new Date(effectiveEnd).toLocaleString(),
                overlaps
            });
        }
        
        return overlaps;
    });

    console.log(`Camera ${camera.id}: Found ${relevantRecordings.length} relevant recordings`);

    return (
        <Card variant="soft" sx={{ mb: 1 }}>
            <CardContent sx={{ py: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                    <Videocam sx={{ fontSize: 16, color: 'success.500' }} />
                    <Typography level="body-sm" fontWeight="bold" sx={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        maxWidth: '200px'
                    }}>
                        {camera.device_name || `Camera ${camera.hub_camera_id}`}
                    </Typography>
                    <Chip size="sm" variant="soft" color="primary">
                        {relevantRecordings.length} recordings
                    </Chip>
                </Stack>
                <Box sx={{ 
                    height: '20px', 
                    backgroundColor: 'neutral.200', 
                    borderRadius: 'sm',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {relevantRecordings.map((recording, index) => {
                        const recStart = recording.started_at || recording.start_ms || recording.start_time;
                        const recEnd = recording.ended_at || recording.end_ms || recording.end_time ||
                                      (recording.started_at || recording.start_ms) + (recording.duration_ms || 0);
                        
                        const effectiveStart = Math.max(recStart, startTime);
                        const effectiveEnd = Math.min(recEnd || Date.now(), endTime);
                        const left = ((effectiveStart - startTime) / duration) * 100;
                        const width = ((effectiveEnd - effectiveStart) / duration) * 100;
                        
                        console.log(`Recording ${index} positioning:`, {
                            recStart: new Date(recStart).toLocaleString(),
                            recEnd: recEnd ? new Date(recEnd).toLocaleString() : 'ongoing',
                            effectiveStart: new Date(effectiveStart).toLocaleString(),
                            effectiveEnd: new Date(effectiveEnd).toLocaleString(),
                            left: `${left}%`,
                            width: `${width}%`
                        });
                        
                        return (
                            <Box
                                key={`${recording.id || index}-${index}`}
                                sx={{
                                    position: 'absolute',
                                    left: `${left}%`,
                                    width: `${width}%`,
                                    height: '100%',
                                    backgroundColor: 'success.400',
                                    opacity: 0.8,
                                    minWidth: '2px' // Ensure very short recordings are visible
                                }}
                            />
                        );
                    })}
                    {relevantRecordings.length === 0 && (
                        <Box sx={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)'
                        }}>
                            <Typography level="body-xs" textColor="neutral.500">
                                No recordings in this time period
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// Connectivity Lane component
const ConnectivityLane = ({ startTime, endTime, data }) => {
    const rulerDuration = endTime - startTime;
    const CHART_WIDTH = 800;
    const CHART_HEIGHT = 100;
    
    // Filter data to only include records within the ruler timeframe
    const relevantData = data.filter(point => 
        point.timestamp >= startTime && point.timestamp <= endTime
    );
    
    console.log(`Connectivity Lane Debug:`, {
        totalDataPoints: data.length,
        relevantDataPoints: relevantData.length,
        timeWindow: {
            start: new Date(startTime).toLocaleString(),
            end: new Date(endTime).toLocaleString()
        },
        sampleConnectivity: relevantData.slice(0, 3).map(point => ({
            timestamp: new Date(point.timestamp).toLocaleString(),
            wifi_connected: point.wifi_connected,
            wifi_signal_strength: point.wifi_signal_strength,
            ethernet_connected: point.ethernet_connected,
            ethernet_speed: point.ethernet_speed
        }))
    });

    // Create gap detection similar to other charts
    const createConnectivityGaps = () => {
        const gaps = [];
        const GAP_THRESHOLD = 30 * 1000; // 30 seconds in milliseconds
        
        for (let i = 1; i < relevantData.length; i++) {
            const timeDiff = Math.abs(relevantData[i].timestamp - relevantData[i - 1].timestamp);
            
            if (timeDiff > GAP_THRESHOLD) {
                const prevPoint = relevantData[i - 1];
                const currentPoint = relevantData[i];
                
                const prevTimeRatio = (prevPoint.timestamp - startTime) / rulerDuration;
                const currentTimeRatio = (currentPoint.timestamp - startTime) / rulerDuration;
                
                const gapStartX = prevTimeRatio * CHART_WIDTH;
                const gapEndX = currentTimeRatio * CHART_WIDTH;
                const gapWidth = Math.abs(gapEndX - gapStartX);
                const gapLeftX = Math.min(gapStartX, gapEndX);
                
                gaps.push({
                    x: gapLeftX,
                    width: gapWidth,
                    height: CHART_HEIGHT,
                    duration: timeDiff
                });
            }
        }
        
        return gaps;
    };

    const gaps = createConnectivityGaps();

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Wifi sx={{ color: 'info.500' }} />
                    <Typography level="title-sm" fontWeight="bold">Connectivity</Typography>
                    <Chip size="sm" variant="soft" color="info">
                        {relevantData.length} points
                    </Chip>
                    {gaps.length > 0 && (
                        <Chip size="sm" variant="soft" color="warning">
                            {gaps.length} gaps
                        </Chip>
                    )}
                </Stack>
                <Box sx={{ 
                    height: '100px', 
                    backgroundColor: 'neutral.50', 
                    borderRadius: 'sm',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'neutral.200'
                }}>
                    <svg 
                        width="100%" 
                        height="100%" 
                        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                        preserveAspectRatio="none"
                        style={{ position: 'absolute' }}
                    >
                        {/* Background grid */}
                        <defs>
                            <pattern id="connectivity-grid" width="40" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#e0e0e0" strokeWidth="0.5" opacity="0.3"/>
                            </pattern>
                        </defs>
                        <rect width={CHART_WIDTH} height={CHART_HEIGHT} fill="url(#connectivity-grid)" />
                        
                        {/* Render gap rectangles first (behind the data) */}
                        {gaps.map((gap, gapIndex) => (
                            <g key={`connectivity-gap-${gapIndex}`}>
                                {/* Gap rectangle spanning full height */}
                                <rect
                                    x={gap.x}
                                    y={0}
                                    width={gap.width}
                                    height={gap.height}
                                    fill="#f5f5f5"
                                    opacity="0.8"
                                    stroke="#e0e0e0"
                                    strokeWidth="1"
                                    strokeDasharray="3,3"
                                />
                                {/* "No Data" text - only show if gap is wide enough */}
                                {gap.width > 50 && (
                                    <text
                                        x={gap.x + gap.width / 2}
                                        y={gap.height / 2}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fontSize="10"
                                        fill="#999"
                                        fontFamily="Arial, sans-serif"
                                    >
                                        No Data
                                    </text>
                                )}
                            </g>
                        ))}
                        
                        {/* Connection status indicators */}
                        {relevantData.map((point, index) => {
                            // Use same X positioning logic as other charts
                            const timeRatio = (point.timestamp - startTime) / rulerDuration;
                            const xPixels = timeRatio * CHART_WIDTH;
                            
                            // WiFi takes 85% of chart height (top portion)
                            const WIFI_HEIGHT_PERCENT = 0.85;
                            const ETHERNET_HEIGHT_PERCENT = 0.1; // Small band at bottom
                            const MARGIN = 0.05; // 5% margin at very bottom
                            
                            // Check if completely disconnected (both WiFi and Ethernet off)
                            const completelyDisconnected = !point.wifi_connected && !point.ethernet_connected;
                            
                            if (completelyDisconnected) {
                                // Show prominent red line spanning full height for complete disconnection
                                return (
                                    <g key={`connectivity-${point.timestamp}-${index}`}>
                                        <rect
                                            x={xPixels - 1}
                                            y={0}
                                            width="3" // Slightly wider for visibility
                                            height={CHART_HEIGHT}
                                            fill="#f44336" // Red for complete disconnection
                                            opacity="0.9"
                                        />
                                        <circle
                                            cx={xPixels}
                                            cy={CHART_HEIGHT / 2}
                                            r="2"
                                            fill="#f44336"
                                            opacity="1"
                                        />
                                    </g>
                                );
                            }
                            
                            // WiFi signal strength bar (0-100% signal maps to 0-85% of chart height)
                            const wifiStrength = point.wifi_connected ? point.wifi_signal_strength : 0;
                            const wifiBarHeight = (wifiStrength / 100) * (CHART_HEIGHT * WIFI_HEIGHT_PERCENT);
                            const wifiY = CHART_HEIGHT * (1 - WIFI_HEIGHT_PERCENT - ETHERNET_HEIGHT_PERCENT - MARGIN); // Start after margins
                            
                            // Ethernet indicator (small bar at bottom)
                            const ethernetBarHeight = point.ethernet_connected ? 
                                CHART_HEIGHT * ETHERNET_HEIGHT_PERCENT : 0; // No bar when disconnected
                            const ethernetY = CHART_HEIGHT * (1 - ETHERNET_HEIGHT_PERCENT - MARGIN); // Bottom area
                            
                            return (
                                <g key={`connectivity-${point.timestamp}-${index}`}>
                                    {/* WiFi signal strength bar - only show when connected */}
                                    {point.wifi_connected && (
                                        <>
                                            <rect
                                                x={xPixels - 1}
                                                y={wifiY + (CHART_HEIGHT * WIFI_HEIGHT_PERCENT - wifiBarHeight)} // Bottom-aligned within WiFi area
                                                width="2"
                                                height={wifiBarHeight}
                                                fill="#2196f3" // Blue for WiFi
                                                opacity="0.8"
                                            />
                                            <circle
                                                cx={xPixels}
                                                cy={wifiY + (CHART_HEIGHT * WIFI_HEIGHT_PERCENT - wifiBarHeight)}
                                                r="1.5"
                                                fill="#2196f3"
                                                opacity="0.9"
                                            />
                                        </>
                                    )}
                                    
                                    {/* Ethernet connection indicator - only show when connected */}
                                    {point.ethernet_connected && (
                                        <>
                                            <rect
                                                x={xPixels - 1}
                                                y={ethernetY}
                                                width="2"
                                                height={ethernetBarHeight}
                                                fill="#4caf50" // Green for Ethernet
                                                opacity="0.8"
                                            />
                                            <circle
                                                cx={xPixels}
                                                cy={ethernetY + (ethernetBarHeight / 2)}
                                                r="1"
                                                fill="#4caf50"
                                                opacity="0.9"
                                            />
                                        </>
                                    )}
                                </g>
                            );
                        })}
                        
                        {/* Reference line between WiFi and Ethernet areas */}
                        <line
                            x1={0}
                            y1={CHART_HEIGHT * 0.9} // Line at 90% (separating WiFi from Ethernet)
                            x2={CHART_WIDTH}
                            y2={CHART_HEIGHT * 0.9}
                            stroke="#e0e0e0"
                            strokeWidth="1"
                            strokeDasharray="4,4"
                            opacity="0.5"
                        />
                    </svg>
                    
                    {/* Y-axis labels */}
                    <Typography level="body-xs" sx={{ 
                        position: 'absolute', 
                        top: '2px', 
                        left: '5px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        fontSize: '10px'
                    }}>
                        100%
                    </Typography>
                    <Typography level="body-xs" sx={{ 
                        position: 'absolute', 
                        top: '15%', 
                        left: '5px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        fontSize: '10px',
                        color: '#2196f3',
                        fontWeight: 'bold'
                    }}>
                        WiFi Signal
                    </Typography>
                    <Typography level="body-xs" sx={{ 
                        position: 'absolute', 
                        bottom: '5px', 
                        left: '5px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        fontSize: '10px',
                        color: '#4caf50',
                        fontWeight: 'bold'
                    }}>
                        Ethernet
                    </Typography>
                    <Typography level="body-xs" sx={{ 
                        position: 'absolute', 
                        bottom: '2px', 
                        right: '5px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        fontSize: '10px'
                    }}>
                        0%
                    </Typography>
                    
                    {relevantData.length === 0 && (
                        <Box sx={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)'
                        }}>
                            <Typography level="body-xs" textColor="neutral.500">
                                No connectivity data available for this time period
                            </Typography>
                        </Box>
                    )}
                </Box>
                
                {/* Legend */}
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: '12px', height: '12px', backgroundColor: '#2196f3', borderRadius: '2px' }} />
                        <Typography level="body-xs">WiFi</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: '12px', height: '12px', backgroundColor: '#4caf50', borderRadius: '2px' }} />
                        <Typography level="body-xs">Ethernet</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: '12px', height: '12px', backgroundColor: '#f44336', borderRadius: '2px' }} />
                        <Typography level="body-xs">Disconnected</Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

// Telemetry Chart Lane component
const TelemetryChartLane = ({ title, icon, data, startTime, endTime, metrics, unit = '%', color = 'primary' }) => {
    const rulerDuration = endTime - startTime;
    
    // Define chart dimensions in pixels
    const CHART_WIDTH = 800;  // Fixed width for consistent calculations
    const CHART_HEIGHT = 100; // Fixed height
    
    console.log(`Chart positioning for "${title}" - PIXEL-BASED:`, {
        ruler: {
            start: new Date(startTime).toLocaleString(),
            end: new Date(endTime).toLocaleString(),
            duration: rulerDuration / (60 * 1000) + ' minutes'
        },
        chartDimensions: {
            width: CHART_WIDTH + 'px',
            height: CHART_HEIGHT + 'px'
        },
        dataInfo: data.length > 0 ? {
            start: new Date(Math.min(...data.map(p => p.timestamp))).toLocaleString(),
            end: new Date(Math.max(...data.map(p => p.timestamp))).toLocaleString(),
            totalPoints: data.length
        } : { message: 'No data' }
    });
    
    // Filter data points to ONLY include those within the ruler timeframe
    const relevantData = data.filter(point => 
        point.timestamp >= startTime && point.timestamp <= endTime
    );
    
    console.log(`Filtered data for "${title}":`, {
        totalPoints: data.length,
        relevantPoints: relevantData.length,
        filteredOut: data.length - relevantData.length,
        ruler: {
            start: new Date(startTime).toLocaleString(),
            end: new Date(endTime).toLocaleString()
        },
        dataInWindow: relevantData.length > 0 ? {
            first: new Date(relevantData[0].timestamp).toLocaleString(),
            last: new Date(relevantData[relevantData.length - 1].timestamp).toLocaleString(),
            span: (relevantData[relevantData.length - 1].timestamp - relevantData[0].timestamp) / (60 * 1000) + ' minutes'
        } : 'No data in ruler timeframe'
    });
    
    // Debug logging for timestamp alignment
    console.log(`TelemetryChartLane "${title}" Debug:`, {
        totalDataPoints: data.length,
        relevantDataPoints: relevantData.length,
        timeWindow: {
            startTime: new Date(startTime).toLocaleString(),
            endTime: new Date(endTime).toLocaleString(),
            durationMs: endTime - startTime,
            durationMin: (endTime - startTime) / (60 * 1000)
        },
        dataTimeSpan: relevantData.length > 0 ? {
            firstPoint: new Date(relevantData[0].timestamp).toLocaleString(),
            lastPoint: new Date(relevantData[relevantData.length - 1].timestamp).toLocaleString(),
            spanMs: relevantData[relevantData.length - 1].timestamp - relevantData[0].timestamp,
            spanMin: (relevantData[relevantData.length - 1].timestamp - relevantData[0].timestamp) / (60 * 1000)
        } : null,
        samplePoints: relevantData.slice(0, 5).map(point => ({
            timestamp: new Date(point.timestamp).toLocaleString(),
            timestampMs: point.timestamp,
            rulerPositionPercent: ((point.timestamp - startTime) / rulerDuration) * 100,
            calculation: {
                pointTime: point.timestamp,
                rulerStart: startTime,
                rulerEnd: endTime,
                rulerDuration: rulerDuration,
                timeDiffFromRulerStart: point.timestamp - startTime,
                ratio: (point.timestamp - startTime) / rulerDuration
            }
        })),
        lastPoints: relevantData.slice(-3).map(point => ({
            timestamp: new Date(point.timestamp).toLocaleString(),
            timestampMs: point.timestamp,
            rulerPositionPercent: ((point.timestamp - startTime) / rulerDuration) * 100
        }))
    });

    // Create SVG path for each metric
    const createPath = (metricKey) => {
        if (relevantData.length < 2) {
            console.log(`⚠️ Insufficient data for ${metricKey}: ${relevantData.length} points`);
            return { pathSegments: [], gaps: [] }; // Return both paths and gaps
        }
        
        // Define Y-axis ranges based on chart type
        let minValue, maxValue;
        
        if (title === "Performance") {
            // Performance: Always 0% to 100%
            minValue = 0;
            maxValue = 100;
        } else if (title === "Storage Usage") {
            // Storage: 0% to 100%
            minValue = 0;
            maxValue = 100;
        } else {
            // Memory, Temperature, Fan Speed, Power: Use dynamic scaling
            const values = relevantData.map(point => point[metricKey]).filter(v => v != null);
            minValue = Math.min(...values);
            maxValue = Math.max(...values);
        }
        
        const valueRange = maxValue - minValue || 1; // Avoid division by zero
        
        // Create path segments, breaking on gaps > 30 seconds
        const pathSegments = [];
        const gaps = []; // Store gap information for visualization
        let currentSegment = [];
        const GAP_THRESHOLD = 30 * 1000; // 30 seconds in milliseconds
        
        console.log(`🔍 SEGMENT FORMATION DEBUG for ${title} - ${metricKey}:`, {
            totalDataPoints: relevantData.length,
            gapThreshold: GAP_THRESHOLD / 1000 + 's',
            dataTimeSpan: relevantData.length > 0 ? {
                start: new Date(relevantData[0].timestamp).toLocaleString(),
                end: new Date(relevantData[relevantData.length - 1].timestamp).toLocaleString(),
                totalSpanSec: (relevantData[relevantData.length - 1].timestamp - relevantData[0].timestamp) / 1000
            } : null
        });
        
        relevantData.forEach((point, index) => {
            // PIXEL-BASED X positioning: Convert ruler alignment to absolute pixels
            const timeRatio = (point.timestamp - startTime) / rulerDuration;
            const xPixels = timeRatio * CHART_WIDTH;
            
            // Calculate Y position in pixels with proper scaling (invert for SVG coordinates)
            const value = point[metricKey];
            let yPixels;
            if (value == null) {
                yPixels = CHART_HEIGHT / 2; // Middle position for null values
            } else {
                // Scale value between 10% and 90% of chart height for better visibility
                const normalizedValue = (value - minValue) / valueRange;
                yPixels = CHART_HEIGHT * 0.9 - (normalizedValue * CHART_HEIGHT * 0.8); // 10% margin at top and bottom
            }
            
            // Check for gap with previous point
            let gapDetected = false;
            if (index > 0) {
                const timeDiff = point.timestamp - relevantData[index - 1].timestamp;
                const absoluteTimeDiff = Math.abs(timeDiff); // Use absolute difference to handle any sort order
                
                if (absoluteTimeDiff > GAP_THRESHOLD) {
                    gapDetected = true;
                    
                    // Calculate gap visualization coordinates
                    const prevPoint = relevantData[index - 1];
                    const prevTimeRatio = (prevPoint.timestamp - startTime) / rulerDuration;
                    const prevXPixels = prevTimeRatio * CHART_WIDTH;
                    
                    const gapStartX = prevXPixels;
                    const gapEndX = xPixels;
                    const gapWidth = Math.abs(gapEndX - gapStartX);
                    const gapLeftX = Math.min(gapStartX, gapEndX);
                    
                    // Add gap info for visualization
                    gaps.push({
                        x: gapLeftX,
                        width: gapWidth,
                        height: CHART_HEIGHT,
                        startTime: Math.min(prevPoint.timestamp, point.timestamp),
                        endTime: Math.max(prevPoint.timestamp, point.timestamp),
                        duration: absoluteTimeDiff
                    });
                    
                    // Gap detected - finish current segment and start new one
                    if (currentSegment.length > 0) {
                        pathSegments.push([...currentSegment]);
                        console.log(`🔥 GAP DETECTED - Finishing segment ${pathSegments.length}:`, {
                            gapDuration: `${(absoluteTimeDiff / 1000).toFixed(1)}s`,
                            rawTimeDiff: `${(timeDiff / 1000).toFixed(1)}s`,
                            threshold: `${GAP_THRESHOLD / 1000}s`,
                            dataOrder: timeDiff > 0 ? 'chronological' : 'reverse-chronological',
                            gapVisualization: {
                                x: gapLeftX.toFixed(2),
                                width: gapWidth.toFixed(2),
                                startTime: new Date(Math.min(prevPoint.timestamp, point.timestamp)).toLocaleString(),
                                endTime: new Date(Math.max(prevPoint.timestamp, point.timestamp)).toLocaleString()
                            },
                            previousPoint: {
                                index: index - 1,
                                timestamp: relevantData[index - 1].timestamp,
                                date: new Date(relevantData[index - 1].timestamp).toLocaleString()
                            },
                            currentPoint: {
                                index: index,
                                timestamp: point.timestamp,
                                date: new Date(point.timestamp).toLocaleString()
                            },
                            finishedSegmentLength: currentSegment.length,
                            finishedSegmentPoints: currentSegment.slice(0, 3).map(p => `(${p})`) // First 3 points
                        });
                        currentSegment = []; // Start new segment
                    }
                }
                
                // Log time differences for first few points to understand data order
                if (title === "Temperature" && index < 5) {
                    console.log(`⏱️ Time diff analysis - Point ${index}:`, {
                        previousTime: new Date(relevantData[index - 1].timestamp).toLocaleString(),
                        currentTime: new Date(point.timestamp).toLocaleString(),
                        rawDiff: `${(timeDiff / 1000).toFixed(1)}s`,
                        absoluteDiff: `${(absoluteTimeDiff / 1000).toFixed(1)}s`,
                        gapDetected: gapDetected,
                        threshold: `${GAP_THRESHOLD / 1000}s`,
                        dataOrder: timeDiff > 0 ? 'chronological (newer→older)' : 'reverse-chronological (older→newer)'
                    });
                }
            }
            
            // Add current point to segment (either continuing or starting new after gap)
            const pointString = `${xPixels.toFixed(2)},${yPixels.toFixed(2)}`;
            currentSegment.push(pointString);
            
            // Log every point for temperature to see segment formation
            if (title === "Temperature" && index < 10) { // Only first 10 points to avoid spam
                console.log(`📍 Point ${index} (${metricKey}):`, {
                    timestamp: new Date(point.timestamp).toLocaleString(),
                    value: value,
                    coordinates: pointString,
                    gapDetected: gapDetected,
                    currentSegmentLength: currentSegment.length,
                    totalSegmentsSoFar: pathSegments.length,
                    timeSincePrevious: index > 0 ? `${((Math.abs(point.timestamp - relevantData[index - 1].timestamp)) / 1000).toFixed(1)}s` : 'N/A'
                });
            }
        });
        
        // Add final segment if it exists
        if (currentSegment.length > 0) {
            pathSegments.push(currentSegment);
            console.log(`✅ Final segment added for ${title} - ${metricKey}:`, {
                segmentLength: currentSegment.length,
                totalSegments: pathSegments.length
            });
        }
        
        // Return array of path segments instead of joined string
        const pathSegmentsArray = pathSegments
            .filter(segment => segment && segment.length > 1) // Only valid segments with 2+ points
            .map((segment, segmentIndex) => {
                const pathString = `M ${segment.join(' L ')}`;
                
                // Log each segment creation for temperature
                if (title === "Temperature") {
                    console.log(`🎨 Creating SVG path segment ${segmentIndex + 1}:`, {
                        segmentIndex: segmentIndex,
                        pointCount: segment.length,
                        firstPoint: segment[0],
                        lastPoint: segment[segment.length - 1],
                        pathString: pathString.substring(0, 100) + '...',
                        fullPathLength: pathString.length
                    });
                }
                
                return pathString;
            });
            
        console.log(`🔧 FINAL SEGMENTS for ${title} - ${metricKey}:`, {
            originalDataPoints: relevantData.length,
            rawSegments: pathSegments.length,
            validSegments: pathSegmentsArray.length,
            gapsFound: gaps.length,
            segmentSummary: pathSegments.map((seg, idx) => ({
                segmentIndex: idx,
                pointCount: seg.length,
                valid: seg.length > 1
            })),
            gapSummary: gaps.map((gap, idx) => ({
                gapIndex: idx,
                duration: `${(gap.duration / 1000).toFixed(1)}s`,
                width: gap.width.toFixed(2) + 'px'
            }))
        });
        
        // Return both path segments and gaps
        return { pathSegments: Array.isArray(pathSegmentsArray) ? pathSegmentsArray : [], gaps };
    };

    return (
        <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    {icon}
                    <Typography level="title-sm" fontWeight="bold">{title}</Typography>
                    <Chip size="sm" variant="soft" color={color}>
                        {relevantData.length} points
                    </Chip>
                </Stack>
                <Box sx={{ 
                    height: '100px', 
                    backgroundColor: 'neutral.50', 
                    borderRadius: 'sm',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'neutral.200'
                }}>
                    <svg 
                        width="100%" 
                        height="100%" 
                        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                        preserveAspectRatio="none"
                        style={{ position: 'absolute' }}
                    >
                        {/* Grid lines for better alignment visualization */}
                        <defs>
                            <pattern id={`grid-${title.replace(/\s+/g, '-')}`} width="40" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#e0e0e0" strokeWidth="0.5" opacity="0.3"/>
                            </pattern>
                        </defs>
                        <rect width={CHART_WIDTH} height={CHART_HEIGHT} fill={`url(#grid-${title.replace(/\s+/g, '-')})`} />
                        
                        {/* Data lines */}
                        {metrics.map((metric, index) => {
                            const { pathSegments, gaps } = createPath(metric.key);
                            
                            if (title === "Temperature") {
                                console.log(`🎨 Rendering ${metric.key}:`, {
                                    segmentCount: pathSegments?.length || 0,
                                    pathElements: pathSegments?.length || 0,
                                    gapsCount: gaps?.length || 0,
                                    isArray: Array.isArray(pathSegments),
                                    pathSegmentsType: typeof pathSegments
                                });
                            }
                            
                            // Safety check: ensure pathSegments is an array
                            if (!Array.isArray(pathSegments)) {
                                console.error(`❌ pathSegments is not an array for ${metric.key}:`, pathSegments);
                                return null;
                            }
                            
                            return (
                                <React.Fragment key={`${metric.key}-fragments`}>
                                    {/* Render gap rectangles first (behind the lines) */}
                                    {gaps && gaps.length > 0 && gaps.map((gap, gapIndex) => (
                                        <g key={`${metric.key}-gap-group-${gapIndex}`}>
                                            {/* Gap rectangle */}
                                            <rect
                                                x={gap.x}
                                                y={0}
                                                width={gap.width}
                                                height={gap.height}
                                                fill="#f5f5f5"
                                                opacity="0.8"
                                                stroke="#e0e0e0"
                                                strokeWidth="1"
                                                strokeDasharray="3,3"
                                            />
                                            {/* "No Data" text - only show if gap is wide enough */}
                                            {gap.width > 50 && (
                                                <text
                                                    x={gap.x + gap.width / 2}
                                                    y={gap.height / 2}
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                    fontSize="10"
                                                    fill="#999"
                                                    fontFamily="Arial, sans-serif"
                                                >
                                                    No Data
                                                </text>
                                            )}
                                            {/* Gap duration text - only show if gap is very wide */}
                                            {gap.width > 80 && (
                                                <text
                                                    x={gap.x + gap.width / 2}
                                                    y={gap.height / 2 + 12}
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                    fontSize="8"
                                                    fill="#bbb"
                                                    fontFamily="Arial, sans-serif"
                                                >
                                                    {(gap.duration / 1000 / 60).toFixed(1)}min gap
                                                </text>
                                            )}
                                        </g>
                                    ))}
                                    
                                    {/* Render data line segments */}
                                    {pathSegments.map((pathString, segmentIndex) => (
                                        <path
                                            key={`${metric.key}-segment-${segmentIndex}`}
                                            d={pathString}
                                            fill="none"
                                            stroke={metric.color}
                                            strokeWidth="2"
                                            opacity="0.8"
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        
                        {/* Data points for better visibility */}
                        {relevantData.length <= 100 && metrics.map((metric) => 
                            relevantData.map((point, index) => {
                                // PIXEL-BASED X positioning for data points
                                const timeRatio = (point.timestamp - startTime) / rulerDuration;
                                const xPixels = timeRatio * CHART_WIDTH;
                                
                                // Use same Y-axis ranges as the lines
                                let minValue, maxValue;
                                if (title === "Performance") {
                                    minValue = 0; maxValue = 100;
                                } else if (title === "Storage Usage") {
                                    minValue = 0; maxValue = 100;
                                } else {
                                    const values = relevantData.map(p => p[metric.key]).filter(v => v != null);
                                    minValue = Math.min(...values); maxValue = Math.max(...values);
                                }
                                
                                const valueRange = maxValue - minValue || 1;
                                const value = point[metric.key];
                                
                                if (value == null) return null;
                                
                                const normalizedValue = (value - minValue) / valueRange;
                                const yPixels = CHART_HEIGHT * 0.9 - (normalizedValue * CHART_HEIGHT * 0.8);
                                
                                return (
                                    <circle
                                        key={`${metric.key}-point-${index}`}
                                        cx={xPixels}
                                        cy={yPixels}
                                        r="1.5"
                                        fill={metric.color}
                                        opacity="0.7"
                                    />
                                );
                            })
                        )}
                    </svg>
                    
                    {/* Y-axis value indicators */}
                    {relevantData.length > 0 && metrics.length > 0 && (() => {
                        // Use same Y-axis ranges for labels
                        let minValue, maxValue;
                        if (title === "Performance") {
                            minValue = 0; maxValue = 100;
                        } else if (title === "Storage Usage") {
                            minValue = 0; maxValue = 100;
                        } else {
                            const firstMetric = metrics[0];
                            const values = relevantData.map(point => point[firstMetric.key]).filter(v => v != null);
                            minValue = Math.min(...values);
                            maxValue = Math.max(...values);
                        }
                        
                        return (
                            <>
                                <Typography level="body-xs" sx={{ 
                                    position: 'absolute', 
                                    top: '5px', 
                                    left: '5px',
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    fontSize: '10px'
                                }}>
                                    {maxValue.toFixed(1)}{unit}
                                </Typography>
                                <Typography level="body-xs" sx={{ 
                                    position: 'absolute', 
                                    bottom: '5px', 
                                    left: '5px',
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    padding: '2px 4px',
                                    borderRadius: '2px',
                                    fontSize: '10px'
                                }}>
                                    {minValue.toFixed(1)}{unit}
                                </Typography>
                            </>
                        );
                    })()}
                    
                    {relevantData.length === 0 && (
                        <Box sx={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)'
                        }}>
                            <Typography level="body-xs" textColor="neutral.500">
                                No data available for this time period
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    {metrics.map((metric) => (
                        <Stack key={metric.key} direction="row" alignItems="center" spacing={1}>
                            <Box sx={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: metric.color, 
                                borderRadius: '2px' 
                            }} />
                            <Typography level="body-xs">{metric.label}</Typography>
                        </Stack>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

const HubTelemetryPage = () => {
    const { hubId } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    
    const [hub, setHub] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [telemetryData, setTelemetryData] = useState([]);
    const [aiTelemetryData, setAiTelemetryData] = useState([]);
    const [eventsData, setEventsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [telemetryPagination, setTelemetryPagination] = useState(null);
    const [aiTelemetryPagination, setAiTelemetryPagination] = useState(null);
    const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, type: '' });
    
    // Time window state with configurable size
    const [currentTime] = useState(Date.now());
    const [timeWindowEnd, setTimeWindowEnd] = useState(currentTime);
    const [windowSize, setWindowSize] = useState(60); // Default 1 hour in minutes
    const timeWindowStart = timeWindowEnd - (windowSize * 60 * 1000); // Convert minutes to milliseconds
    
    // Window size options
    const windowSizeOptions = [
        { value: 15, label: '15 min', shortLabel: '15m' },
        { value: 60, label: '1 hour', shortLabel: '1h' },
        { value: 180, label: '3 hours', shortLabel: '3h' }
    ];

    // Load hub info and cameras
    useEffect(() => {
        const loadHubData = async () => {
            try {
                const [hubRes, camerasRes] = await Promise.all([
                    fetch(`${API_BASE}/device/${hubId}`, {
                        headers: {
                            'Authorization': session?.accessToken,
                            'Vendor-Id': session?.vendorId,
                        }
                    }),
                    fetch(`${API_BASE}/device/${hubId}/cameras`, {
                        headers: {
                            'Authorization': session?.accessToken,
                            'Vendor-Id': session?.vendorId,
                        }
                    })
                ]);

                if (!hubRes.ok || !camerasRes.ok) {
                    throw new Error('Failed to load hub data');
                }

                const hubData = await hubRes.json();
                const camerasData = await camerasRes.json();

                setHub(hubData);

                // Load camera history for each camera
                const safeCamerasData = Array.isArray(camerasData) ? camerasData : [];
                console.log('Loading camera history for cameras:', safeCamerasData.map(c => ({ id: c.id, name: c.device_name })));
                
                const camerasWithHistory = await Promise.all(
                    safeCamerasData.map(async (cam) => {
                        try {
                            const historyUrl = `${API_BASE}/camera/${cam.id}/history`;
                            console.log(`Fetching camera history from: ${historyUrl}`);
                            
                            const historyRes = await fetch(historyUrl, {
                                headers: {
                                    'Authorization': session?.accessToken,
                                    'Vendor-Id': session?.vendorId,
                                }
                            });
                            
                            console.log(`Camera ${cam.id} history response status:`, historyRes.status);
                            
                            if (historyRes.ok) {
                                const history = await historyRes.json();
                                console.log(`Camera ${cam.id} history data:`, {
                                    isArray: Array.isArray(history),
                                    length: history?.length || 0,
                                    sample: history?.slice(0, 2)
                                });
                                return { ...cam, history: Array.isArray(history) ? history : [] };
                            } else {
                                console.log(`Camera ${cam.id} history request failed:`, historyRes.status, historyRes.statusText);
                                return { ...cam, history: [] };
                            }
                        } catch (err) {
                            console.error(`Failed to load history for camera ${cam.id}:`, err);
                            return { ...cam, history: [] };
                        }
                    })
                );

                console.log('Cameras with history loaded:', camerasWithHistory.map(c => ({ 
                    id: c.id, 
                    name: c.device_name, 
                    historyCount: c.history?.length || 0 
                })));
                
                setCameras(camerasWithHistory);
            } catch (err) {
                console.error('Failed to load hub data:', err);
                setError('Failed to load hub information. Please try again.');
            }
        };

        if (session?.accessToken && session?.vendorId) {
            loadHubData();
        }
    }, [hubId, session]);

    // Load telemetry data for the current time window with new API pagination
    useEffect(() => {
        const loadTelemetryData = async () => {
            try {
                setLoading(true);
                setError(null);
                setLoadingProgress({ current: 0, total: 0, type: 'telemetry' });

                console.log('🔄 Starting telemetry data loading with new API:', {
                    startTime: new Date(timeWindowStart).toLocaleString(),
                    endTime: new Date(timeWindowEnd).toLocaleString(),
                    from: timeWindowStart,
                    to: timeWindowEnd,
                    durationMs: timeWindowEnd - timeWindowStart,
                    durationMin: (timeWindowEnd - timeWindowStart) / (60 * 1000)
                });

                const {
                    items: allTelemetryRecords,
                    firstPageMeta,
                    pageCount,
                } = await getResourceUtilization(
                    hubId,
                    timeWindowStart,
                    timeWindowEnd,
                    session,
                    {
                        onPage: (page) => {
                            setLoadingProgress((prev) => ({ ...prev, current: page, type: 'telemetry' }));
                        },
                    }
                );

                if (firstPageMeta) {
                    setTelemetryPagination(firstPageMeta);
                }

                console.log('✅ All resource utilization data loaded:', {
                    totalPages: pageCount,
                    totalRecords: allTelemetryRecords.length,
                    timeSpan: allTelemetryRecords.length > 0 ? {
                        earliest: new Date(Math.min(...allTelemetryRecords.map(r => r.timestamp))).toLocaleString(),
                        latest: new Date(Math.max(...allTelemetryRecords.map(r => r.timestamp))).toLocaleString(),
                        spanMs: Math.max(...allTelemetryRecords.map(r => r.timestamp)) - Math.min(...allTelemetryRecords.map(r => r.timestamp)),
                        spanMin: (Math.max(...allTelemetryRecords.map(r => r.timestamp)) - Math.min(...allTelemetryRecords.map(r => r.timestamp))) / (60 * 1000)
                    } : null
                });

                setTelemetryData(allTelemetryRecords);

                // Load AI telemetry data for the same time window with new API
                try {
                    setLoadingProgress({ current: 0, total: 0, type: 'ai-telemetry' });
                    
                    let allAiRecords = [];
                    let currentAiCursor = null;
                    let aiPageCount = 0;
                    let totalAiRecordsLoaded = 0;

                    console.log('🔄 Starting AI telemetry data loading with new API');

                    while (true) {
                        aiPageCount++;
                        setLoadingProgress(prev => ({ ...prev, current: aiPageCount, type: 'ai-telemetry' }));

                        const aiParams = new URLSearchParams({
                            from: timeWindowStart.toString(),
                            to: timeWindowEnd.toString(),
                            limit: '1000'
                        });

                        if (currentAiCursor) {
                            aiParams.append('before', currentAiCursor.toString());
                        }

                        console.log(`📄 Loading AI telemetry page ${aiPageCount}:`, {
                            from: new Date(timeWindowStart).toLocaleString(),
                            to: new Date(timeWindowEnd).toLocaleString(),
                            before: currentAiCursor ? new Date(currentAiCursor).toLocaleString() : 'none',
                            apiUrl: `${API_BASE}/device/${hubId}/ai-telemetry?${aiParams}`
                        });

                        const aiTelemetryRes = await fetch(`${API_BASE}/device/${hubId}/ai-telemetry?${aiParams}`, {
                            headers: {
                                'Authorization': session?.accessToken,
                                'Vendor-Id': session?.vendorId,
                                'Content-Type': 'application/json'
                            }
                        });

                        if (!aiTelemetryRes.ok) {
                            console.log(`AI Telemetry page ${aiPageCount} request failed:`, aiTelemetryRes.status, aiTelemetryRes.statusText);
                            break;
                        }

                        const aiTelemetryResponse = await aiTelemetryRes.json();
                        const aiItems = aiTelemetryResponse.items || [];
                        const aiCount = aiTelemetryResponse.count || 0;
                        const aiHasMore = aiTelemetryResponse.has_more || false;
                        const aiNextCursor = aiTelemetryResponse.next_cursor;
                        const aiRefreshCursor = aiTelemetryResponse.refresh_cursor;

                        console.log(`📊 AI Telemetry page ${aiPageCount} loaded:`, {
                            itemsInPage: aiItems.length,
                            count: aiCount,
                            totalSoFar: totalAiRecordsLoaded + aiItems.length,
                            hasMore: aiHasMore,
                            nextCursor: aiNextCursor ? new Date(aiNextCursor).toLocaleString() : null,
                            refreshCursor: aiRefreshCursor ? new Date(aiRefreshCursor).toLocaleString() : null,
                            timeRange: aiItems.length > 0 ? {
                                firstRecord: new Date(aiItems[0].processing_start_ms).toLocaleString(),
                                lastRecord: new Date(aiItems[aiItems.length - 1].processing_start_ms).toLocaleString()
                            } : null
                        });

                        // Add items to our collection
                        allAiRecords = [...allAiRecords, ...aiItems];
                        totalAiRecordsLoaded += aiItems.length;

                        // Store pagination info for the first page
                        if (aiPageCount === 1) {
                            setAiTelemetryPagination({
                                count: aiCount,
                                has_more: aiHasMore,
                                next_cursor: aiNextCursor,
                                refresh_cursor: aiRefreshCursor
                            });
                        }

                        // Check if we need to continue
                        if (!aiHasMore || aiItems.length === 0) {
                            console.log(`✅ AI Telemetry pagination complete. Reason: ${!aiHasMore ? 'No more older data' : 'Empty page'}`);
                            break;
                        }

                        // Update cursor for next page
                        currentAiCursor = aiNextCursor;
                        
                        if (!currentAiCursor) {
                            console.log('⚠️ No AI next_cursor available for next page, stopping pagination');
                            break;
                        }

                        // Safety check
                        if (aiPageCount > 100) {
                            console.warn('🛑 Stopping AI pagination after 100 pages to prevent infinite loop');
                            break;
                        }

                        // Add small delay
                        await new Promise(resolve => setTimeout(resolve, 50));
                    }

                    console.log('✅ All AI telemetry data loaded:', {
                        totalPages: aiPageCount,
                        totalRecords: allAiRecords.length,
                        timeSpan: allAiRecords.length > 0 ? {
                            earliest: new Date(Math.min(...allAiRecords.map(r => r.processing_start_ms))).toLocaleString(),
                            latest: new Date(Math.max(...allAiRecords.map(r => r.processing_start_ms))).toLocaleString()
                        } : null
                    });

                    setAiTelemetryData(allAiRecords);
                } catch (aiErr) {
                    console.error('Failed to load AI telemetry data:', aiErr);
                    setAiTelemetryData([]);
                }

                try {
                    const eventsResponse = await getEvents(
                        hubId,
                        {
                            from: timeWindowStart,
                            to: timeWindowEnd,
                            limit: 10,
                        },
                        session
                    );
                    setEventsData(eventsResponse.items || []);
                } catch (eventsErr) {
                    console.error('Failed to load events:', eventsErr);
                    setEventsData([]);
                }
            } catch (err) {
                console.error('Failed to load telemetry data:', err);
                setError('Failed to load telemetry data. Please try again.');
            } finally {
                setLoading(false);
                setLoadingProgress({ current: 0, total: 0, type: '' });
            }
        };

        if (session?.accessToken && session?.vendorId) {
            loadTelemetryData();
        }
    }, [hubId, session, timeWindowStart, timeWindowEnd, windowSize]);

    const navigateTime = (direction) => {
        const windowSizeInMs = windowSize * 60 * 1000; // Convert minutes to milliseconds
        if (direction === 'past') {
            setTimeWindowEnd(prev => prev - windowSizeInMs);
        } else if (direction === 'future' && timeWindowEnd < currentTime) {
            setTimeWindowEnd(prev => Math.min(prev + windowSizeInMs, currentTime));
        }
    };

    if (error) {
        return (
            <Sheet sx={{ p: 4 }}>
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                    <Typography>{error}</Typography>
                </Alert>
                <Button variant="outlined" onClick={() => window.location.reload()}>
                    <Refresh sx={{ mr: 1 }} />
                    Retry
                </Button>
            </Sheet>
        );
    }

    return (
        <Sheet sx={{ p: 4, maxWidth: '1600px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <IconButton 
                        variant="outlined" 
                        onClick={() => navigate(`/hub/${hubId}`)}
                        sx={{ borderRadius: '50%' }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <DeviceHub sx={{ fontSize: 32, color: 'primary.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            {hub?.name || `Hub ${hubId}`} - Telemetry
                        </Typography>
                        <Typography level="body-sm" textColor="neutral.500">
                            Hardware ID: {hub?.hardware_id}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Time Navigation */}
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Timeline sx={{ color: 'primary.500' }} />
                            <Typography level="h4">Timeline View</Typography>
                            
                            {/* Window Size Selector */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography level="body-sm" textColor="neutral.600">
                                    Window:
                                </Typography>
                                <Stack direction="row" spacing={0.5}>
                                    {windowSizeOptions.map((option) => (
                                        <Button
                                            key={option.value}
                                            size="sm"
                                            variant={windowSize === option.value ? "solid" : "outlined"}
                                            color={windowSize === option.value ? "primary" : "neutral"}
                                            onClick={() => setWindowSize(option.value)}
                                        >
                                            {option.shortLabel}
                                        </Button>
                                    ))}
                                </Stack>
                            </Stack>
                        </Stack>
                        
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography level="body-sm" textColor="neutral.600">
                                {new Date(timeWindowStart).toLocaleString()} - {new Date(timeWindowEnd).toLocaleString()}
                            </Typography>
                            
                            {/* Data summary */}
                            {(telemetryData.length > 0 || aiTelemetryData.length > 0) && (
                                <Stack direction="row" spacing={1}>
                                    {telemetryData.length > 0 && (
                                        <Chip size="sm" variant="soft" color="primary">
                                            {telemetryData.length} telemetry points
                                        </Chip>
                                    )}
                                    {aiTelemetryData.length > 0 && (
                                        <Chip size="sm" variant="soft" color="secondary">
                                            {aiTelemetryData.length} AI records
                                        </Chip>
                                    )}
                                </Stack>
                            )}
                            
                            <Stack direction="row" spacing={1}>
                                <IconButton 
                                    variant="outlined" 
                                    onClick={() => navigateTime('past')}
                                    size="sm"
                                    title={`Go back ${windowSizeOptions.find(opt => opt.value === windowSize)?.label}`}
                                >
                                    <ChevronLeft />
                                </IconButton>
                                <IconButton 
                                    variant="outlined" 
                                    onClick={() => navigateTime('future')}
                                    disabled={timeWindowEnd >= currentTime}
                                    size="sm"
                                    title={`Go forward ${windowSizeOptions.find(opt => opt.value === windowSize)?.label}`}
                                >
                                    <ChevronRight />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {loading ? (
                <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size="lg" />
                    <Typography level="body-lg">
                        {loadingProgress.type === 'telemetry' ? 
                            `Loading telemetry data (page ${loadingProgress.current})...` :
                        loadingProgress.type === 'ai-telemetry' ? 
                            `Loading AI telemetry data (page ${loadingProgress.current})...` :
                            'Loading telemetry data...'
                        }
                    </Typography>
                    {loadingProgress.current > 0 && (
                        <Typography level="body-sm" textColor="neutral.500">
                            Fetching all data for the selected time window
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Box>
                    {/* Timeline Ruler */}
                    <TimelineRuler startTime={timeWindowStart} endTime={timeWindowEnd} />

                    <EventsCompactFeed hubId={hubId} events={eventsData} loading={loading} />

                    {/* AI Processing */}
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ py: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                                <SmartToy sx={{ color: 'primary.500' }} />
                                <Typography level="title-sm" fontWeight="bold">AI Processing Efficiency</Typography>
                                <Chip size="sm" variant="soft" color="primary">
                                    {aiTelemetryData?.length || 0} total records
                                </Chip>
                            </Stack>
                            
                            {/* AI Processing Lane */}
                            <AIProcessingLane 
                                aiData={aiTelemetryData} 
                                startTime={timeWindowStart} 
                                endTime={timeWindowEnd} 
                                rulerDuration={timeWindowEnd - timeWindowStart} 
                            />
                            
                            {/* Legend */}
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ width: '12px', height: '12px', backgroundColor: '#4caf50', borderRadius: '2px' }} />
                                    <Typography level="body-xs">≤100% (Efficient)</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ width: '12px', height: '12px', backgroundColor: '#ff9800', borderRadius: '2px' }} />
                                    <Typography level="body-xs">101-200% (Moderate)</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ width: '12px', height: '12px', backgroundColor: '#f44336', borderRadius: '2px' }} />
                                    <Typography level="body-xs">&gt;200% (High Load)</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{ width: '12px', height: '2px', backgroundColor: '#1976d2', borderRadius: '1px' }} />
                                    <Typography level="body-xs">Median</Typography>
                                </Stack>
                            </Stack>

                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                <AIProcessingTimeLane
                                    aiData={aiTelemetryData}
                                    startTime={timeWindowStart}
                                    endTime={timeWindowEnd}
                                    rulerDuration={timeWindowEnd - timeWindowStart}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Camera Recording Lanes */}
                    <Box sx={{ mb: 3 }}>
                        <Typography level="title-md" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                            <Videocam sx={{ mr: 1, color: 'success.500' }} />
                            Camera Recordings
                        </Typography>
                        {cameras.map((camera) => (
                            <CameraRecordingLane 
                                key={camera.id} 
                                camera={camera} 
                                startTime={timeWindowStart} 
                                endTime={timeWindowEnd} 
                            />
                        ))}
                        {cameras.length === 0 && (
                            <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2 }}>
                                No cameras found for this hub.
                            </Typography>
                        )}
                    </Box>

                    {/* Telemetry Chart Lanes */}
                    <Box>
                        <Typography level="title-md" sx={{ mb: 2 }}>System Metrics</Typography>
                        
                        {/* Performance Metrics */}
                        <TelemetryChartLane
                            title="Performance"
                            icon={<Computer sx={{ color: 'primary.500' }} />}
                            data={telemetryData}
                            startTime={timeWindowStart}
                            endTime={timeWindowEnd}
                            metrics={[
                                { key: 'cpu_usage_avg', label: 'CPU Usage', color: '#1976d2' },
                                { key: 'gpu_usage', label: 'GPU Usage', color: '#388e3c' }
                            ]}
                            unit="%"
                            color="primary"
                        />

                        {/* Memory Usage */}
                        <TelemetryChartLane
                            title="Memory Usage"
                            icon={<Memory sx={{ color: 'info.500' }} />}
                            data={telemetryData}
                            startTime={timeWindowStart}
                            endTime={timeWindowEnd}
                            metrics={[
                                { key: 'ram_used_mb', label: 'Used Memory', color: '#1976d2' },
                                { key: 'ram_total_mb', label: 'Total Memory', color: '#757575' }
                            ]}
                            unit="MB"
                            color="info"
                        />

                        {/* Temperature Metrics */}
                        <TelemetryChartLane
                            title="Temperature"
                            icon={<Thermostat sx={{ color: 'warning.500' }} />}
                            data={telemetryData}
                            startTime={timeWindowStart}
                            endTime={timeWindowEnd}
                            metrics={[
                                { key: 'cpu_temp', label: 'CPU Temp', color: '#d32f2f' },
                                { key: 'gpu_temp', label: 'GPU Temp', color: '#f57c00' },
                                { key: 'soc_temp', label: 'SoC Temp', color: '#7b1fa2' }
                            ]}
                            unit="°C"
                            color="warning"
                        />

                        {/* Fan Speed */}
                        <TelemetryChartLane
                            title="Fan Speed"
                            icon={<Air sx={{ color: 'secondary.500' }} />}
                            data={telemetryData}
                            startTime={timeWindowStart}
                            endTime={timeWindowEnd}
                            metrics={[
                                { key: 'fan_speed_percent', label: 'Fan Speed', color: '#9c27b0' }
                            ]}
                            unit="%"
                            color="secondary"
                        />

                        {/* Connectivity Metrics */}
                        <ConnectivityLane 
                            startTime={timeWindowStart} 
                            endTime={timeWindowEnd} 
                            data={telemetryData} 
                        />

                        {/* Power Metrics */}
                        <TelemetryChartLane
                            title="Power Consumption"
                            icon={<BatteryChargingFull sx={{ color: 'success.500' }} />}
                            data={telemetryData}
                            startTime={timeWindowStart}
                            endTime={timeWindowEnd}
                            metrics={[
                                { key: 'power_total_mw', label: 'Total Power', color: '#1976d2' },
                                { key: 'power_cpu_mw', label: 'CPU Power', color: '#388e3c' }
                            ]}
                            unit="mW"
                            color="success"
                        />

                        {/* Storage Metrics */}
                        <TelemetryChartLane
                            title="Storage Usage"
                            icon={<Storage sx={{ color: 'neutral.500' }} />}
                            data={telemetryData.map(point => ({
                                ...point,
                                fs_used_percentage: (point.fs_used_mb / point.fs_total_mb) * 100,
                                media_used_percentage: (point.media_used_mb / point.media_total_mb) * 100
                            }))}
                            startTime={timeWindowStart}
                            endTime={timeWindowEnd}
                            metrics={[
                                { key: 'fs_used_percentage', label: 'File System', color: '#1976d2' },
                                { key: 'media_used_percentage', label: 'Media Storage', color: '#388e3c' }
                            ]}
                            unit="%"
                            color="neutral"
                        />
                    </Box>
                </Box>
            )}
        </Sheet>
    );
};

export default HubTelemetryPage; 